import React, { useState } from 'react';
import { X, MessageSquare, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

// Use relative URL for same-origin API calls
const API = '/api';

const FeedbackSurvey = ({ isOpen, onClose, user }) => {
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState({
    ease_of_use: '',
    most_helpful_feature: '',
    missing_features: '',
    would_recommend: '',
    overall_feedback: '',
    would_purchase: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please provide a star rating');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        user_id: user.id,
        rating,
        ...feedback
      });
      
      toast.success('Thank you for your valuable feedback! 🙏');
      onClose();
      
      // Reset form
      setRating(0);
      setFeedback({
        ease_of_use: '',
        most_helpful_feature: '',
        missing_features: '',
        would_recommend: '',
        overall_feedback: '',
        would_purchase: ''
      });
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200 bg-gradient-to-r from-[#881337] to-[#9F1239]">
          <div className="flex items-center gap-3">
            <MessageSquare className="text-white" size={24} />
            <h2 className="text-2xl font-serif font-bold text-white">Share Your Feedback</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <strong>Thank you for testing VowFlow!</strong> Your honest feedback helps us build a better product for wedding planners like you. This survey takes about 3-5 minutes.
            </p>
          </div>

          {/* Star Rating */}
          <div>
            <label className="text-[#1C1917] font-medium mb-3 block">
              Overall Experience Rating <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={40}
                    className={star <= rating ? 'fill-[#881337] text-[#881337]' : 'text-stone-300'}
                  />
                </button>
              ))}
            </div>
            <p className="text-xs text-stone-500 mt-1">
              {rating === 0 && 'Click to rate'}
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </p>
          </div>

          {/* Ease of Use */}
          <div>
            <label className="text-[#1C1917] font-medium mb-2 block">
              How easy was VowFlow to use?
            </label>
            <select
              value={feedback.ease_of_use}
              onChange={(e) => setFeedback({ ...feedback, ease_of_use: e.target.value })}
              className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
            >
              <option value="">Select one...</option>
              <option value="very_easy">Very Easy - Intuitive and clear</option>
              <option value="easy">Easy - Figured it out quickly</option>
              <option value="moderate">Moderate - Needed some time to learn</option>
              <option value="difficult">Difficult - Confusing navigation</option>
              <option value="very_difficult">Very Difficult - Hard to use</option>
            </select>
          </div>

          {/* Most Helpful Feature */}
          <div>
            <label className="text-[#1C1917] font-medium mb-2 block">
              What feature did you find most helpful?
            </label>
            <textarea
              value={feedback.most_helpful_feature}
              onChange={(e) => setFeedback({ ...feedback, most_helpful_feature: e.target.value })}
              placeholder="e.g., AI timeline generator, budget planner, guest list management..."
              rows={3}
              className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
            />
          </div>

          {/* Missing Features */}
          <div>
            <label className="text-[#1C1917] font-medium mb-2 block">
              What features are missing that you'd like to see?
            </label>
            <textarea
              value={feedback.missing_features}
              onChange={(e) => setFeedback({ ...feedback, missing_features: e.target.value })}
              placeholder="Be specific - this helps us prioritize development..."
              rows={3}
              className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
            />
          </div>

          {/* Would Recommend */}
          <div>
            <label className="text-[#1C1917] font-medium mb-2 block">
              Would you recommend VowFlow to other planners?
            </label>
            <select
              value={feedback.would_recommend}
              onChange={(e) => setFeedback({ ...feedback, would_recommend: e.target.value })}
              className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
            >
              <option value="">Select one...</option>
              <option value="definitely">Definitely - It's excellent</option>
              <option value="probably">Probably - With some improvements</option>
              <option value="maybe">Maybe - Needs significant work</option>
              <option value="probably_not">Probably Not</option>
              <option value="definitely_not">Definitely Not</option>
            </select>
          </div>

          {/* Would Purchase */}
          <div>
            <label className="text-[#1C1917] font-medium mb-2 block">
              Would you purchase VowFlow at $9.99-$14.99/month?
            </label>
            <select
              value={feedback.would_purchase}
              onChange={(e) => setFeedback({ ...feedback, would_purchase: e.target.value })}
              className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
            >
              <option value="">Select one...</option>
              <option value="yes_immediately">Yes, immediately</option>
              <option value="yes_with_improvements">Yes, with some improvements</option>
              <option value="maybe">Maybe - Need to think about it</option>
              <option value="no_too_expensive">No, it's too expensive</option>
              <option value="no_not_valuable">No, not enough value</option>
            </select>
          </div>

          {/* Overall Feedback */}
          <div>
            <label className="text-[#1C1917] font-medium mb-2 block">
              Any other feedback or suggestions?
            </label>
            <textarea
              value={feedback.overall_feedback}
              onChange={(e) => setFeedback({ ...feedback, overall_feedback: e.target.value })}
              placeholder="Bugs, design feedback, feature ideas, anything else..."
              rows={4}
              className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || rating === 0}
              className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackSurvey;
